import { api } from './api'
import {
  Protocol,
  ProtocolFormData,
  ProtocolEnrollment,
  ProtocolEligibilityResult,
  ProtocolScreenResult,
  ProtocolComplianceMetrics,
  ComplianceReport,
} from '../types/protocol'

export interface ComplianceFilters {
  startDate?: string
  endDate?: string
  patientIds?: string[]
  status?: string[]
}

export const protocolService = {
  /**
   * Get all protocols
   */
  getAll: async (): Promise<Protocol[]> => {
    const response = await api.get<Protocol[]>('/protocols')
    return response.data
  },

  /**
   * Get a single protocol by ID
   */
  getById: async (id: string): Promise<Protocol> => {
    const response = await api.get<Protocol>(`/protocols/${id}`)
    return response.data
  },

  /**
   * Create a new protocol
   */
  create: async (data: ProtocolFormData): Promise<Protocol> => {
    const response = await api.post<Protocol>('/protocols', data)
    return response.data
  },

  /**
   * Update an existing protocol
   */
  update: async (id: string, data: Partial<ProtocolFormData>): Promise<Protocol> => {
    const response = await api.put<Protocol>(`/protocols/${id}`, data)
    return response.data
  },

  /**
   * Delete a protocol
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/protocols/${id}`)
  },

  /**
   * Get all protocols for a patient
   */
  getPatientProtocols: async (patientId: string): Promise<ProtocolEnrollment[]> => {
    const response = await api.get<ProtocolEnrollment[]>(`/patients/${patientId}/protocols`)
    return response.data
  },

  /**
   * Check if patient is eligible for a protocol
   */
  checkEligibility: async (patientId: string, protocolId: string): Promise<ProtocolEligibilityResult> => {
    const response = await api.get<ProtocolEligibilityResult>(
      `/protocol-eligibility/check?patientId=${patientId}&protocolId=${protocolId}`
    )
    return response.data
  },

  /**
   * Screen practice population for eligible patients (not already enrolled)
   */
  screenEligiblePatients: async (protocolId: string, limit?: number): Promise<ProtocolScreenResult> => {
    const params = new URLSearchParams()
    params.append('protocolId', protocolId)
    if (limit != null) params.append('limit', String(limit))
    const response = await api.get<ProtocolScreenResult>(
      `/protocol-eligibility/screen?${params.toString()}`
    )
    return response.data
  },

  /**
   * Enroll a patient in a protocol
   */
  enrollPatient: async (patientId: string, protocolId: string, notes?: string): Promise<ProtocolEnrollment> => {
    const response = await api.post<ProtocolEnrollment>(`/patients/${patientId}/protocols`, {
      protocolId,
      notes,
    })
    return response.data
  },

  /**
   * Exclude a patient from a protocol
   */
  excludePatient: async (
    patientId: string,
    protocolId: string,
    reason?: string
  ): Promise<ProtocolEnrollment> => {
    const response = await api.put<ProtocolEnrollment>(`/patients/${patientId}/protocols/${protocolId}/exclude`, {
      reason,
    })
    return response.data
  },

  /**
   * Get compliance for a protocol
   */
  getCompliance: async (protocolId: string): Promise<ProtocolComplianceMetrics> => {
    const response = await api.get<ProtocolComplianceMetrics>(`/protocols/${protocolId}/compliance`)
    return response.data
  },

  /**
   * Get compliance report for a protocol
   */
  getComplianceReport: async (protocolId: string, filters?: ComplianceFilters): Promise<ComplianceReport> => {
    const params = new URLSearchParams()
    if (filters?.startDate) params.append('startDate', filters.startDate)
    if (filters?.endDate) params.append('endDate', filters.endDate)
    if (filters?.patientIds) filters.patientIds.forEach((id) => params.append('patientIds', id))
    if (filters?.status) filters.status.forEach((s) => params.append('status', s))

    const response = await api.get<ComplianceReport>(
      `/protocols/${protocolId}/compliance/report?${params.toString()}`
    )
    return response.data
  },

  /**
   * Get population compliance for multiple protocols
   */
  getPopulationCompliance: async (
    protocolIds: string[],
    filters?: ComplianceFilters
  ): Promise<ProtocolComplianceMetrics[]> => {
    const params = new URLSearchParams()
    protocolIds.forEach((id) => params.append('protocolIds', id))
    if (filters?.startDate) params.append('startDate', filters.startDate)
    if (filters?.endDate) params.append('endDate', filters.endDate)

    const response = await api.get<ProtocolComplianceMetrics[]>(
      `/protocol-compliance/population?${params.toString()}`
    )
    return response.data
  },
}
